import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { ArrowLeft, Users, Calendar, Download, ChevronDown, ChevronRight, Trash2, Loader2, Lock } from 'lucide-react';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { canViewFullText, REDACTED_TEXT, REDACTED_EXPLANATION, hasAdminAccess } from '../../lib/permissions';

interface AnsweredQuestionMeta {
  title: string;
  sectionTitle: string;
  type: string;
  scaleType?: string;
  followUpQuestion?: string;
}

interface EngagementPulseResponse {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  templateId: string;
  templateName: string;
  responses: { [questionId: string]: any };
  answeredQuestions?: { [questionId: string]: AnsweredQuestionMeta };
  reviewCycle?: string;
  status: 'completed' | 'in_progress';
  submittedAt: any;
  createdAt: any;
}

interface EngagementPulseResponsesProps {
  /**
   * 'admin' (default) — full list, gated to admins/HR, includes delete; back nav goes to /admin.
   * 'manager' — filtered to current user's direct reports only, no delete, back nav goes to /dashboard.
   */
  mode?: 'admin' | 'manager';
}

const EngagementPulseResponses: React.FC<EngagementPulseResponsesProps> = ({ mode = 'admin' }) => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [responses, setResponses] = useState<EngagementPulseResponse[]>([]);
  const [allUsersManagers, setAllUsersManagers] = useState<{ [userId: string]: Array<{ id: string; name: string; email: string }> }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedResponses, setExpandedResponses] = useState<Set<string>>(new Set());
  const [exportingPdfId, setExportingPdfId] = useState<string | null>(null);

  // Permission checks
  const isAdmin = hasAdminAccess(currentUser?.email, userProfile);
  const hasAccess = mode === 'admin' ? isAdmin : !!currentUser;
  const backPath = mode === 'admin' ? '/admin' : '/dashboard';
  const backLabel = mode === 'admin' ? 'Back to Admin' : 'Back to Dashboard';
  const headerSubtitle = mode === 'admin'
    ? 'View and export engagement survey responses'
    : 'View engagement pulse responses from your direct reports';

  useEffect(() => {
    if (hasAccess) {
      fetchResponses();
    }
  }, [currentUser, hasAccess]);

  // For manager mode: filter to submissions whose author lists the current user as a manager.
  const visibleResponses = mode === 'manager' && currentUser
    ? responses.filter(r =>
        (allUsersManagers[r.userId] || []).some(m => m.id === currentUser.uid)
      )
    : responses;

  const fetchResponses = async () => {
    try {
      setLoading(true);
      
      const { collection, getDocs, orderBy, query } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Fetch all users to get manager relationships
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const managersMap: { [userId: string]: Array<{ id: string; name: string; email: string }> } = {};
      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        managersMap[doc.id] = data.managers || [];
      });
      setAllUsersManagers(managersMap);
      
      const responsesRef = collection(db, 'engagementPulseSubmissions');
      const responsesQuery = query(responsesRef, orderBy('submittedAt', 'desc'));
      const snapshot = await getDocs(responsesQuery);
      
      const fetchedResponses: EngagementPulseResponse[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      })) as EngagementPulseResponse[];
      
      setResponses(fetchedResponses);
      console.log(`Loaded ${fetchedResponses.length} engagement pulse responses`);
    } catch (err: any) {
      console.error('Error fetching engagement pulse responses:', err);
      setError('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const formatAnswer = (answer: any, meta?: AnsweredQuestionMeta): string => {
    if (answer === undefined || answer === null) return 'No response';
    
    let displayAnswer = Array.isArray(answer) ? answer.join(', ') : String(answer);
    
    if (meta?.type === 'rating' && meta?.scaleType) {
      if (meta.scaleType === 'fill') {
        displayAnswer = `${answer}%`;
      } else if (meta.scaleType === 'likert') {
        const likertLabels = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
        displayAnswer = likertLabels[Number(answer) - 1] || displayAnswer;
      } else if (meta.scaleType === 'frequency') {
        const frequencyLabels = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];
        displayAnswer = frequencyLabels[Number(answer) - 1] || displayAnswer;
      }
    }
    
    return displayAnswer;
  };

  /** Check if viewer can see full text for a specific response */
  const canSeeFullText = (response: EngagementPulseResponse): boolean => {
    if (!currentUser) return false;
    return canViewFullText(
      currentUser.uid,
      currentUser.email,
      userProfile,
      response.userId,
      allUsersManagers[response.userId] || []
    );
  };

  const exportResponseToPdf = async (response: EngagementPulseResponse) => {
    if (!canSeeFullText(response)) {
      alert('You do not have permission to export the full text of this response. Only HR personnel and direct managers can export response content.');
      return;
    }
    
    setExportingPdfId(response.id);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      
      // Helper function to add a new page if needed
      const checkNewPage = (requiredSpace: number = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to add wrapped text
      const addWrappedText = (text: string, fontSize: number = 10): number => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        const lineHeight = fontSize * 0.4;
        
        for (let i = 0; i < lines.length; i++) {
          if (yPosition + lineHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(lines[i], margin, yPosition);
          yPosition += lineHeight;
        }
        
        return yPosition;
      };

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Engagement Pulse Response', margin, yPosition);
      yPosition += 10;
      
      // User info header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0);
      pdf.text(response.userName || 'Unknown User', margin, yPosition);
      yPosition += 6;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text(response.userEmail || '', margin, yPosition);
      yPosition += 4;
      pdf.text(`Submitted: ${response.submittedAt ? response.submittedAt.toLocaleDateString() + ' at ' + response.submittedAt.toLocaleTimeString() : 'In progress'}`, margin, yPosition);
      yPosition += 4;
      pdf.text(`Status: ${response.status}`, margin, yPosition);
      if (response.reviewCycle) {
        yPosition += 4;
        pdf.text(`Review Cycle: ${response.reviewCycle}`, margin, yPosition);
      }
      yPosition += 4;
      pdf.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, yPosition);
      pdf.setTextColor(0);
      yPosition += 8;
      
      // Draw a separator line
      pdf.setDrawColor(200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      
      // Questions and answers
      const questionIds = Object.keys(response.responses)
        .filter(k => !k.endsWith('_comments') && !k.endsWith('_followup'));
      
      for (const questionId of questionIds) {
        const answer = response.responses[questionId];
        const meta = response.answeredQuestions?.[questionId];
        const comments = response.responses[`${questionId}_comments`];
        const followUp = response.responses[`${questionId}_followup`];
        
        checkNewPage(30);
        
        // Section title (if available)
        if (meta?.sectionTitle) {
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 100, 200);
          pdf.text(meta.sectionTitle, margin, yPosition);
          pdf.setTextColor(0);
          yPosition += 6;
        }
        
        // Question title
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        addWrappedText(meta?.title || `Question: ${questionId}`, 10);
        yPosition += 2;
        
        // Answer
        pdf.setFont('helvetica', 'normal');
        const displayAnswer = formatAnswer(answer, meta);
        
        // Draw response in a light background box
        const responseLines = pdf.splitTextToSize(displayAnswer, maxWidth - 8);
        const responseHeight = responseLines.length * 4 + 4;
        
        checkNewPage(responseHeight + 10);
        
        pdf.setFillColor(245, 245, 245);
        pdf.rect(margin, yPosition - 2, maxWidth, responseHeight, 'F');
        
        yPosition += 2;
        pdf.setFontSize(10);
        for (const line of responseLines) {
          pdf.text(line, margin + 2, yPosition);
          yPosition += 4;
        }
        yPosition += 2;
        
        // Follow-up question response
        if (meta?.followUpQuestion && followUp) {
          checkNewPage(20);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(50, 50, 150);
          addWrappedText(meta.followUpQuestion, 9);
          pdf.setTextColor(0);
          
          pdf.setFont('helvetica', 'normal');
          const followUpLines = pdf.splitTextToSize(followUp, maxWidth - 8);
          const followUpHeight = followUpLines.length * 3.5 + 4;
          
          checkNewPage(followUpHeight + 5);
          
          pdf.setFillColor(235, 240, 255);
          pdf.rect(margin, yPosition - 2, maxWidth, followUpHeight, 'F');
          
          yPosition += 2;
          for (const line of followUpLines) {
            pdf.text(line, margin + 2, yPosition);
            yPosition += 3.5;
          }
          yPosition += 2;
        }
        
        // Additional comments
        if (comments) {
          checkNewPage(20);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Additional Comments:', margin, yPosition);
          yPosition += 4;
          
          pdf.setFont('helvetica', 'normal');
          const commentLines = pdf.splitTextToSize(comments, maxWidth - 8);
          const commentHeight = commentLines.length * 3.5 + 4;
          
          checkNewPage(commentHeight + 5);
          
          pdf.setFillColor(250, 250, 240);
          pdf.rect(margin, yPosition - 2, maxWidth, commentHeight, 'F');
          
          yPosition += 2;
          for (const line of commentLines) {
            pdf.text(line, margin + 2, yPosition);
            yPosition += 3.5;
          }
          yPosition += 2;
        }
        
        yPosition += 6;
      }
      
      // Save the PDF
      const safeName = (response.userName || 'unknown').replace(/\s+/g, '-').toLowerCase();
      const filename = `engagement-pulse-${safeName}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
    } finally {
      setExportingPdfId(null);
    }
  };

  const toggleResponseExpansion = (responseId: string) => {
    const newExpanded = new Set(expandedResponses);
    if (newExpanded.has(responseId)) {
      newExpanded.delete(responseId);
    } else {
      newExpanded.add(responseId);
    }
    setExpandedResponses(newExpanded);
  };

  const deleteResponse = async (responseId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}'s engagement pulse response? This will allow them to retake the survey.`)) {
      return;
    }

    try {
      const { doc, deleteDoc } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const responseRef = doc(db, 'engagementPulseSubmissions', responseId);
      await deleteDoc(responseRef);
      
      // Update local state
      setResponses(prev => prev.filter(r => r.id !== responseId));
      
      // Remove from expanded set if it was expanded
      const newExpanded = new Set(expandedResponses);
      newExpanded.delete(responseId);
      setExpandedResponses(newExpanded);
      
      console.log(`Deleted engagement pulse response for ${userName}`);
    } catch (err: any) {
      console.error('Error deleting response:', err);
      setError(`Failed to delete response: ${err.message}`);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate(backPath)} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                {backLabel}
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  Engagement Pulse Responses
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="text-center py-12">
              <h3 className="text-lg font-medium mb-2">Access Denied</h3>
              <p className="text-muted-foreground mb-4">
                You need admin permissions to view engagement pulse responses.
              </p>
              <Button onClick={() => navigate(backPath)}>
                Return
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading responses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(backPath)} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              {backLabel}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-primary">
                {mode === 'manager' ? 'Team Engagement Pulse Responses' : 'Engagement Pulse Responses'}
              </h1>
              <p className="text-muted-foreground">{headerSubtitle}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Responses</CardTitle>
              <Users className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{visibleResponses.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <Users className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {visibleResponses.filter(r => r.status === 'completed').length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Latest Response</CardTitle>
              <Calendar className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {visibleResponses.length > 0 && visibleResponses[0].submittedAt
                  ? visibleResponses[0].submittedAt.toLocaleDateString()
                  : 'None'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Responses List */}
        <Card>
          <CardHeader>
            <CardTitle>Survey Responses ({visibleResponses.length})</CardTitle>
            <CardDescription>
              {mode === 'manager'
                ? 'Engagement pulse survey submissions from your direct reports'
                : 'All engagement pulse survey submissions'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {visibleResponses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Responses Yet</h3>
                <p>
                  {mode === 'manager'
                    ? 'None of your direct reports have submitted an engagement pulse response yet.'
                    : 'No engagement pulse survey responses have been submitted.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {visibleResponses.map((response) => {
                  const isExpanded = expandedResponses.has(response.id);
                  return (
                    <div key={response.id} className="border rounded-lg">
                      {/* Header - always visible */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleResponseExpansion(response.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <div>
                                <h3 className="font-medium">{response.userName}</h3>
                                <p className="text-sm text-muted-foreground">{response.userEmail}</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                <strong>{Object.keys(response.responses).filter(k => !k.endsWith('_comments') && !k.endsWith('_followup')).length}</strong> questions answered
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {response.submittedAt ? response.submittedAt.toLocaleDateString() : 'In progress'}
                              </p>
                              {response.reviewCycle && (
                                <p className="text-xs text-muted-foreground">
                                  Cycle: {response.reviewCycle}
                                </p>
                              )}
                            </div>
                            <Badge variant={
                              response.status === 'completed' 
                                ? 'default' 
                                : 'secondary'
                            } className={
                              response.status === 'completed'
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                            }>
                              {response.status}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent accordion toggle
                                exportResponseToPdf(response);
                              }}
                              disabled={exportingPdfId === response.id}
                              className="flex items-center gap-1"
                            >
                              {exportingPdfId === response.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                              {exportingPdfId === response.id ? 'Exporting...' : 'Export PDF'}
                            </Button>
                            {mode === 'admin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent accordion toggle
                                  deleteResponse(response.id, response.userName);
                                }}
                                className="flex items-center gap-1 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Expandable content */}
                      {isExpanded && (() => {
                        const fullTextVisible = canSeeFullText(response);
                        return (
                          <div className="border-t bg-muted/20 p-4">
                            <div className="text-sm text-muted-foreground mb-4">
                              Template: {response.templateName}
                            </div>
                            
                            {/* Redaction notice */}
                            {!fullTextVisible && (
                              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-start gap-2 mb-4">
                                <Lock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                  {REDACTED_EXPLANATION}
                                </p>
                              </div>
                            )}
                            
                            {/* All responses */}
                            <div className="space-y-3">
                              <h4 className="font-medium text-sm">Survey Responses:</h4>
                              {Object.entries(response.responses)
                                .filter(([questionId]) => !questionId.endsWith('_comments') && !questionId.endsWith('_followup'))
                                .map(([questionId, answer], index) => {
                                  const meta = response.answeredQuestions?.[questionId];
                                  const comments = response.responses[`${questionId}_comments`];
                                  const followUp = response.responses[`${questionId}_followup`];
                                  
                                  // Format the answer based on scale type
                                  let displayAnswer = fullTextVisible
                                    ? (Array.isArray(answer) ? answer.join(', ') : String(answer))
                                    : REDACTED_TEXT;
                                  
                                  if (fullTextVisible && meta?.type === 'rating' && meta?.scaleType) {
                                    if (meta.scaleType === 'fill') {
                                      displayAnswer = `${answer}%`;
                                    } else if (meta.scaleType === 'likert') {
                                      const likertLabels = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
                                      displayAnswer = likertLabels[Number(answer) - 1] || displayAnswer;
                                    } else if (meta.scaleType === 'frequency') {
                                      const frequencyLabels = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];
                                      displayAnswer = frequencyLabels[Number(answer) - 1] || displayAnswer;
                                    }
                                  }
                                  
                                  return (
                                    <div key={questionId} className="bg-background p-3 rounded border">
                                      {meta?.sectionTitle && (
                                        <div className="text-xs text-muted-foreground mb-1">
                                          {meta.sectionTitle}
                                        </div>
                                      )}
                                      <div className="font-medium text-sm mb-2">
                                        {meta?.title || `Question ${index + 1}`}
                                      </div>
                                      <div className={`text-sm mb-2 ${!fullTextVisible ? 'opacity-60' : ''}`}>
                                        <strong>Response:</strong> {displayAnswer}
                                      </div>
                                      {meta?.followUpQuestion && followUp && (
                                        <div className="text-sm mb-2">
                                          <strong>{meta.followUpQuestion}</strong>
                                          <div className={`mt-1 p-2 bg-blue-50 dark:bg-blue-950/30 rounded text-muted-foreground ${!fullTextVisible ? 'opacity-60' : ''}`}>
                                            {fullTextVisible ? followUp : REDACTED_TEXT}
                                          </div>
                                        </div>
                                      )}
                                      {comments && (
                                        <div className="text-sm">
                                          <strong>Additional Comments:</strong>
                                          <div className={`mt-1 p-2 bg-muted/50 rounded text-muted-foreground ${!fullTextVisible ? 'opacity-60' : ''}`}>
                                            {fullTextVisible ? comments : REDACTED_TEXT}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EngagementPulseResponses;
